import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';

import { generateApolloClientHeaders } from '../../../modules';
import { phoenixAccountGetWalletPaymentInfo } from '../../../modules/graph';
import { CustomerPaymentMethod, useProfileStore } from '../../../modules/store/profileStore';
import { SavedCard, useSavedCardsStore } from '../../../modules/store/savedCardsStore';

interface PageContextSessionData {
  sessionData?: {
    cacheData?: {
      data?: {
        consumerID?: string;
        email?: string;
      };
    };
  };
}

export interface UseWalletSavedCardsArgs {
  pageContext: PageContextSessionData & Record<string, unknown>;
  isLocal: boolean;
  skip?: boolean;
}

// The wallet query returns the BAU CustomerPaymentMethod shape. Some fixtures and callers still
// provide an already-flattened card, so both are accepted when building the list view model.
type WalletPaymentMethod = Partial<CustomerPaymentMethod> & Partial<SavedCard>;

const formatExpiryDate = (expiration?: CustomerPaymentMethod['expiration']): string | undefined => {
  if (!expiration?.month || !expiration?.year) {
    return undefined;
  }
  const month = String(expiration.month).padStart(2, '0');
  const year = String(expiration.year).slice(-2);
  return `${month}/${year}`;
};

const toSavedCard = (method: WalletPaymentMethod): SavedCard => ({
  id: method.id ?? '',
  cardNumber: method.panLast4 ?? method.cardNumber ?? '',
  cardholderName: method.nickname ?? method.cardholderName ?? '',
  cardType: method.type?.code ?? method.issuerType?.code ?? method.cardType ?? '',
  expiryDate: formatExpiryDate(method.expiration) ?? method.expiryDate ?? '',
  isPrimary: method.preferred ?? method.isPrimary ?? false,
  cardStatus: method.cardStatus,
});

export function useWalletSavedCards({ pageContext, isLocal, skip = false }: UseWalletSavedCardsArgs) {
  const isDataLoaded = useRef(false);
  const [hasUxlError, setHasUxlError] = useState(false);

  const sessionData = pageContext?.sessionData?.cacheData?.data;
  const customerId = sessionData?.consumerID;

  const shouldSkip = skip || !customerId;

  // Clear store before fetching fresh data (skip if in mock mode to preserve seeds)
  const shouldSkipRef = useRef(shouldSkip);
  useEffect(() => {
    if (!shouldSkipRef.current) {
      useSavedCardsStore.getState().setProfileSavedCards(undefined as unknown as SavedCard[]);
    }
  }, []);

  const variables = useMemo(
    () => ({
      customerId,
    }),
    [customerId]
  );

  useQuery(phoenixAccountGetWalletPaymentInfo, {
    variables,
    context: generateApolloClientHeaders(isLocal, pageContext),
    fetchPolicy: 'no-cache',
    skip: shouldSkip || isDataLoaded.current,
    onCompleted: (data: Record<string, unknown>) => {
      const walletData = data?.['customer'] as Record<string, unknown> | undefined;
      const paymentMethods =
        ((walletData?.['wallet'] as Record<string, unknown>)?.['paymentMethods'] as WalletPaymentMethod[]) ?? [];

      useSavedCardsStore.getState().setProfileSavedCards(paymentMethods.map(toSavedCard));

      // The BAU AddCard/UpdateCard modals read the card being edited from
      // profileDetails.paymentMethods, which only ProfileSection populates today. Mirror the raw
      // payment methods here so those modals work from the Wallet tab without being forked.
      const { profileDetails, setProfileDetails } = useProfileStore.getState();
      setProfileDetails({
        ...profileDetails,
        paymentMethods: paymentMethods as CustomerPaymentMethod[],
      });

      isDataLoaded.current = true;
      setHasUxlError(false);
    },
    onError: () => {
      isDataLoaded.current = false;
      setHasUxlError(true);
      useSavedCardsStore.getState().setProfileSavedCards([]);
    },
  });

  return { hasUxlError };
}
